import { type Language } from '../i18n';

interface ProjectReportDisplayProps {
  language: Language;
  report: ProjectReport | null;
  isLoading: boolean;
  onNewAssessment: () => void;
}

interface DimensionScore {
  score: number;
  assessment: string;
}

interface BuildVsBuy {
  component: string;
  recommendation: 'build' | 'buy' | 'open-source';
  rationale: string;
}

interface KeyRisk {
  risk: string;
  severity: string;
  likelihood?: string;
  mitigation: string;
}

interface ImmediateAction {
  action: string;
  rationale: string;
  timeline?: string;
}

interface Phase {
  name: string;
  duration: string;
  deliverables: string[];
  team_needed: string;
}

interface ProjectReport {
  metadata: {
    assessment_date: string;
    language: string;
    project_profile: {
      project_summary: string;
      project_type: string;
      target_users: string;
      industry?: string;
      company_size?: string;
    };
  };
  executive_summary?: string[];
  scores: {
    composite_score: number;
    readiness_rating: string;
    dimensions: {
      requirements_clarity?: DimensionScore;
      technical_feasibility?: DimensionScore;
      resource_availability?: DimensionScore;
      budget_realism?: DimensionScore;
      timeline_realism?: DimensionScore;
      integration_complexity?: DimensionScore;
      risk_management?: DimensionScore;
      scalability_planning?: DimensionScore;
      maintenance_planning?: DimensionScore;
    };
  };
  analysis?: {
    project_scope?: string;
    target_users?: string;
    key_requirements?: string[];
    technical_constraints?: string;
    integration_landscape?: string;
  };
  architecture?: {
    recommended_approach?: string;
    approach_rationale?: string;
    suggested_stack?: {
      frontend?: string;
      backend?: string;
      database?: string;
      infrastructure?: string;
      other?: string[];
    };
    build_vs_buy?: BuildVsBuy[];
  };
  development_plan?: {
    mvp_scope?: {
      description?: string;
      core_features?: string[];
      estimated_complexity?: string;
      estimated_duration?: string;
    };
    phases?: {
      phase_1?: Phase;
      phase_2?: Phase;
      phase_3?: Phase;
    };
    estimated_total_effort?: string;
    recommended_team_composition?: string[];
  };
  risks_and_mitigations?: {
    key_strengths?: string[];
    key_risks?: KeyRisk[];
    dependencies?: string[];
  };
  recommendations?: {
    immediate_actions?: ImmediateAction[];
    pre_development_steps?: string[];
    key_decisions_needed?: string[];
  };
  summary: {
    headline: string;
    project_viability?: string;
    primary_recommendation: string;
    partnership_value?: string;
  };
}

function CompositeScore({ score, rating }: { score: number; rating: string }) {
  const getColor = (s: number) => {
    if (s >= 80) return 'text-green-500';
    if (s >= 65) return 'text-blue-500';
    if (s >= 50) return 'text-yellow-500';
    if (s >= 35) return 'text-orange-500';
    return 'text-red-500';
  };

  const getTrackColor = (s: number) => {
    if (s >= 80) return 'stroke-green-500';
    if (s >= 65) return 'stroke-blue-500';
    if (s >= 50) return 'stroke-yellow-500';
    if (s >= 35) return 'stroke-orange-500';
    return 'stroke-red-500';
  };

  const radius = 54;
  const circumference = 2 * Math.PI * radius;
  const progress = (score / 100) * circumference;

  return (
    <div className="flex flex-col items-center">
      <div className="relative w-36 h-36">
        <svg className="w-full h-full -rotate-90" viewBox="0 0 120 120">
          <circle cx="60" cy="60" r={radius} fill="none" stroke="currentColor" className="text-muted/30" strokeWidth="8" />
          <circle
            cx="60" cy="60" r={radius} fill="none"
            className={getTrackColor(score)}
            strokeWidth="8" strokeLinecap="round"
            strokeDasharray={circumference}
            strokeDashoffset={circumference - progress}
            style={{ transition: 'stroke-dashoffset 1s ease-out' }}
          />
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span className={`text-3xl font-bold ${getColor(score)}`}>{Math.round(score)}</span>
          <span className="text-xs text-muted-foreground">/100</span>
        </div>
      </div>
      <span className="mt-2 text-sm font-semibold text-foreground">{rating}</span>
    </div>
  );
}

function ScoreBar({ label, score, assessment }: { label: string; score: number; assessment?: string }) {
  const percentage = (score / 5) * 100;
  const getBarColor = (s: number) => {
    if (s >= 4) return 'bg-green-500';
    if (s >= 3) return 'bg-blue-500';
    if (s >= 2) return 'bg-yellow-500';
    return 'bg-red-500';
  };

  return (
    <div className="mb-3">
      <div className="flex justify-between items-center mb-1">
        <span className="text-xs font-medium text-foreground">{label}</span>
        <span className="text-xs font-bold text-foreground">{score.toFixed(1)}/5</span>
      </div>
      <div className="w-full h-2 bg-muted rounded-full overflow-hidden">
        <div
          className={`h-full rounded-full transition-all duration-700 ${getBarColor(score)}`}
          style={{ width: `${percentage}%` }}
        />
      </div>
      {assessment && <p className="text-xs text-muted-foreground mt-0.5">{assessment}</p>}
    </div>
  );
}

function SeverityBadge({ severity }: { severity: string }) {
  const s = severity.toLowerCase();
  const classes = s === 'high' || s === 'alto'
    ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
    : s === 'medium' || s === 'medio'
      ? 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400'
      : 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400';

  return (
    <span className={`px-2 py-0.5 rounded text-xs font-medium ${classes}`}>
      {severity}
    </span>
  );
}

function RecommendationBadge({ rec }: { rec: string }) {
  const r = rec.toLowerCase();
  const classes = r === 'build'
    ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400'
    : r === 'buy'
      ? 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400'
      : 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400';

  return (
    <span className={`px-2 py-0.5 rounded text-xs font-medium ${classes}`}>
      {rec}
    </span>
  );
}

const dimensionLabels: Record<string, Record<string, string>> = {
  requirements_clarity: { it: 'Chiarezza Requisiti', en: 'Requirements Clarity' },
  technical_feasibility: { it: 'Fattibilità Tecnica', en: 'Technical Feasibility' },
  resource_availability: { it: 'Disponibilità Risorse', en: 'Resource Availability' },
  budget_realism: { it: 'Realismo Budget', en: 'Budget Realism' },
  timeline_realism: { it: 'Realismo Timeline', en: 'Timeline Realism' },
  integration_complexity: { it: 'Complessità Integrazioni', en: 'Integration Complexity' },
  risk_management: { it: 'Gestione Rischi', en: 'Risk Management' },
  scalability_planning: { it: 'Pianificazione Scalabilità', en: 'Scalability Planning' },
  maintenance_planning: { it: 'Pianificazione Manutenzione', en: 'Maintenance Planning' },
};

export function ProjectReportDisplay({ language, report, isLoading, onNewAssessment }: ProjectReportDisplayProps) {
  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center py-20">
        <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mb-4" />
        <p className="text-muted-foreground">
          {language === 'it' ? 'Generazione report in corso...' : 'Generating report...'}
        </p>
      </div>
    );
  }

  if (!report) return null;

  const dims = report.scores.dimensions;

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Executive Summary */}
      {report.executive_summary && report.executive_summary.length > 0 && (
        <div className="card p-4 border-l-4 border-l-primary bg-primary/5">
          <h2 className="text-sm font-semibold text-primary mb-2">
            {language === 'it' ? 'Sintesi Esecutiva' : 'Executive Summary'}
          </h2>
          <ul className="space-y-1.5">
            {report.executive_summary.map((item, i) => (
              <li key={i} className="flex items-start gap-2 text-sm text-foreground">
                <span className="text-primary font-bold mt-0.5">-</span>
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Header */}
      <div className="card p-6">
        <h1 className="text-2xl font-bold text-foreground mb-2">{report.summary.headline}</h1>
        {report.summary.project_viability && (
          <p className="text-muted-foreground">{report.summary.project_viability}</p>
        )}
        <div className="mt-2 text-xs text-muted-foreground">
          {report.metadata.assessment_date} | {report.metadata.project_profile.project_type}
        </div>
      </div>

      {/* Composite Score + Dimensions */}
      <div className="card p-6">
        <div className="flex flex-col md:flex-row gap-8">
          <div className="flex-shrink-0">
            <CompositeScore score={report.scores.composite_score} rating={report.scores.readiness_rating} />
          </div>
          <div className="flex-1">
            <h3 className="text-sm font-semibold text-foreground mb-3">
              {language === 'it' ? 'Punteggi per Dimensione' : 'Dimension Scores'}
            </h3>
            {Object.entries(dims).map(([key, dim]) => {
              if (!dim) return null;
              const label = dimensionLabels[key]?.[language] ?? key;
              return <ScoreBar key={key} label={label} score={dim.score} assessment={dim.assessment} />;
            })}
          </div>
        </div>
      </div>

      {/* Architecture */}
      {report.architecture && (
        <div className="card p-6">
          <h3 className="text-lg font-semibold text-foreground mb-4">
            {language === 'it' ? 'Architettura Suggerita' : 'Suggested Architecture'}
          </h3>
          {report.architecture.recommended_approach && (
            <div className="mb-3">
              <span className="text-xs font-medium text-muted-foreground">
                {language === 'it' ? 'Approccio' : 'Approach'}:
              </span>
              <span className="ml-2 text-sm font-semibold text-foreground capitalize">
                {report.architecture.recommended_approach}
              </span>
            </div>
          )}
          {report.architecture.approach_rationale && (
            <p className="text-sm text-muted-foreground mb-4">{report.architecture.approach_rationale}</p>
          )}

          {/* Tech Stack */}
          {report.architecture.suggested_stack && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
              {report.architecture.suggested_stack.frontend && (
                <div className="p-3 bg-muted/50 rounded-lg">
                  <div className="text-xs font-medium text-muted-foreground">Frontend</div>
                  <div className="text-sm font-semibold text-foreground">{report.architecture.suggested_stack.frontend}</div>
                </div>
              )}
              {report.architecture.suggested_stack.backend && (
                <div className="p-3 bg-muted/50 rounded-lg">
                  <div className="text-xs font-medium text-muted-foreground">Backend</div>
                  <div className="text-sm font-semibold text-foreground">{report.architecture.suggested_stack.backend}</div>
                </div>
              )}
              {report.architecture.suggested_stack.database && (
                <div className="p-3 bg-muted/50 rounded-lg">
                  <div className="text-xs font-medium text-muted-foreground">Database</div>
                  <div className="text-sm font-semibold text-foreground">{report.architecture.suggested_stack.database}</div>
                </div>
              )}
              {report.architecture.suggested_stack.infrastructure && (
                <div className="p-3 bg-muted/50 rounded-lg">
                  <div className="text-xs font-medium text-muted-foreground">Infrastructure</div>
                  <div className="text-sm font-semibold text-foreground">{report.architecture.suggested_stack.infrastructure}</div>
                </div>
              )}
            </div>
          )}

          {/* Build vs Buy */}
          {report.architecture.build_vs_buy && report.architecture.build_vs_buy.length > 0 && (
            <div>
              <h4 className="text-sm font-semibold text-foreground mb-2">
                {language === 'it' ? 'Build vs Buy' : 'Build vs Buy'}
              </h4>
              <div className="space-y-2">
                {report.architecture.build_vs_buy.map((item, idx) => (
                  <div key={idx} className="flex items-start gap-3 p-2 bg-muted/30 rounded-lg">
                    <RecommendationBadge rec={item.recommendation} />
                    <div>
                      <div className="text-sm font-medium text-foreground">{item.component}</div>
                      <div className="text-xs text-muted-foreground">{item.rationale}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Development Plan */}
      {report.development_plan && (
        <div className="card p-6">
          <h3 className="text-lg font-semibold text-foreground mb-4">
            {language === 'it' ? 'Piano di Sviluppo' : 'Development Plan'}
          </h3>

          {/* MVP Scope */}
          {report.development_plan.mvp_scope && (
            <div className="mb-4 p-4 bg-primary/5 border border-primary/20 rounded-lg">
              <h4 className="text-sm font-semibold text-primary mb-2">MVP</h4>
              {report.development_plan.mvp_scope.description && (
                <p className="text-sm text-foreground mb-2">{report.development_plan.mvp_scope.description}</p>
              )}
              {report.development_plan.mvp_scope.core_features && (
                <ul className="list-disc list-inside text-xs text-muted-foreground space-y-1">
                  {report.development_plan.mvp_scope.core_features.map((f, i) => (
                    <li key={i}>{f}</li>
                  ))}
                </ul>
              )}
              <div className="flex gap-4 mt-2">
                {report.development_plan.mvp_scope.estimated_complexity && (
                  <span className="text-xs text-muted-foreground">
                    {language === 'it' ? 'Complessità' : 'Complexity'}: <strong>{report.development_plan.mvp_scope.estimated_complexity}</strong>
                  </span>
                )}
                {report.development_plan.mvp_scope.estimated_duration && (
                  <span className="text-xs text-muted-foreground">
                    {language === 'it' ? 'Durata stimata' : 'Est. Duration'}: <strong>{report.development_plan.mvp_scope.estimated_duration}</strong>
                  </span>
                )}
              </div>
            </div>
          )}

          {/* Phases */}
          {report.development_plan.phases && (
            <div className="space-y-3">
              {[report.development_plan.phases.phase_1, report.development_plan.phases.phase_2, report.development_plan.phases.phase_3]
                .filter((p): p is Phase => !!p)
                .map((phase, idx) => (
                  <div key={idx} className="p-3 border border-border rounded-lg">
                    <div className="flex justify-between items-center mb-2">
                      <h4 className="text-sm font-semibold text-foreground">{phase.name}</h4>
                      <span className="text-xs text-muted-foreground">{phase.duration}</span>
                    </div>
                    <ul className="list-disc list-inside text-xs text-muted-foreground space-y-0.5 mb-1">
                      {phase.deliverables.map((d, i) => <li key={i}>{d}</li>)}
                    </ul>
                    <div className="text-xs text-muted-foreground">
                      {language === 'it' ? 'Team' : 'Team'}: {phase.team_needed}
                    </div>
                  </div>
                ))}
            </div>
          )}

          {report.development_plan.estimated_total_effort && (
            <p className="mt-3 text-sm text-muted-foreground">
              {language === 'it' ? 'Effort totale stimato' : 'Estimated total effort'}: <strong>{report.development_plan.estimated_total_effort}</strong>
            </p>
          )}
        </div>
      )}

      {/* Strengths & Risks */}
      {report.risks_and_mitigations && (
        <div className="card p-6">
          <h3 className="text-lg font-semibold text-foreground mb-4">
            {language === 'it' ? 'Punti di Forza e Rischi' : 'Strengths & Risks'}
          </h3>

          {report.risks_and_mitigations.key_strengths && report.risks_and_mitigations.key_strengths.length > 0 && (
            <div className="mb-4">
              <h4 className="text-sm font-semibold text-green-600 dark:text-green-400 mb-2">
                {language === 'it' ? 'Punti di Forza' : 'Key Strengths'}
              </h4>
              <ul className="space-y-1">
                {report.risks_and_mitigations.key_strengths.map((s, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-foreground">
                    <span className="text-green-500 mt-0.5">+</span>
                    {s}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {report.risks_and_mitigations.key_risks && report.risks_and_mitigations.key_risks.length > 0 && (
            <div>
              <h4 className="text-sm font-semibold text-red-600 dark:text-red-400 mb-2">
                {language === 'it' ? 'Rischi Chiave' : 'Key Risks'}
              </h4>
              <div className="space-y-2">
                {report.risks_and_mitigations.key_risks.map((risk, i) => (
                  <div key={i} className="p-3 bg-muted/30 rounded-lg">
                    <div className="flex items-center gap-2 mb-1">
                      <SeverityBadge severity={risk.severity} />
                      {risk.likelihood && <SeverityBadge severity={risk.likelihood} />}
                      <span className="text-sm font-medium text-foreground">{risk.risk}</span>
                    </div>
                    <p className="text-xs text-muted-foreground">{risk.mitigation}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Recommendations */}
      {report.recommendations && (
        <div className="card p-6">
          <h3 className="text-lg font-semibold text-foreground mb-4">
            {language === 'it' ? 'Raccomandazioni' : 'Recommendations'}
          </h3>

          {report.recommendations.immediate_actions && report.recommendations.immediate_actions.length > 0 && (
            <div className="mb-4">
              <h4 className="text-sm font-semibold text-foreground mb-2">
                {language === 'it' ? 'Azioni Immediate' : 'Immediate Actions'}
              </h4>
              <div className="space-y-2">
                {report.recommendations.immediate_actions.map((action, i) => (
                  <div key={i} className="flex items-start gap-3 p-3 bg-primary/5 border border-primary/20 rounded-lg">
                    <span className="flex-shrink-0 w-6 h-6 bg-primary text-white rounded-full flex items-center justify-center text-xs font-bold">
                      {i + 1}
                    </span>
                    <div>
                      <div className="text-sm font-medium text-foreground">{action.action}</div>
                      <div className="text-xs text-muted-foreground">{action.rationale}</div>
                      {action.timeline && (
                        <div className="text-xs text-primary mt-1">{action.timeline}</div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {report.recommendations.key_decisions_needed && report.recommendations.key_decisions_needed.length > 0 && (
            <div>
              <h4 className="text-sm font-semibold text-foreground mb-2">
                {language === 'it' ? 'Decisioni da Prendere' : 'Key Decisions Needed'}
              </h4>
              <ul className="list-disc list-inside text-sm text-muted-foreground space-y-1">
                {report.recommendations.key_decisions_needed.map((d, i) => <li key={i}>{d}</li>)}
              </ul>
            </div>
          )}
        </div>
      )}

      {/* Summary & CTA */}
      <div className="card p-6 bg-primary-50 dark:bg-primary-50/10">
        <h3 className="text-lg font-semibold text-foreground mb-2">
          {language === 'it' ? 'Prossimi Passi' : 'Next Steps'}
        </h3>
        <p className="text-sm text-foreground mb-3">{report.summary.primary_recommendation}</p>
        {report.summary.partnership_value && (
          <p className="text-xs text-muted-foreground italic">{report.summary.partnership_value}</p>
        )}
        <button
          onClick={onNewAssessment}
          className="mt-4 btn-primary py-2 px-6 text-sm"
        >
          {language === 'it' ? 'Nuova Valutazione' : 'New Assessment'}
        </button>
      </div>
    </div>
  );
}
